import React, { useState, useEffect } from "react";
import { fetchAvailableTimeSlots } from "../../services/bookingService";
import { useTranslation } from "../../hooks/useTranslation";

const LocationPicker = ({
  label,
  onLocationSelect,
  selectedLocation,
  selectedTime,
  onTimeSelect,
  type = "pickup", // "pickup" or "dropoff"
  vehicleId,
  selectedDate,
}) => {
  const { t } = useTranslation('camperDetails');
  const [isOpen, setIsOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);

  // Fetch locations on mount (using today as reference date to get location list)
  useEffect(() => {
    const fetchLocations = async () => {
      if (!vehicleId) return;

      setIsLoadingLocations(true);
      try {
        const isPickup = type === "pickup";
        // Use today's date just to fetch the location list
        const today = new Date();
        const response = await fetchAvailableTimeSlots(vehicleId, today, isPickup);

        // Extract just the location data (not the time slots)
        const locationData = response.map(location => ({
          id: location.location_id,
          name: location.name,
          address: location.address || "Address not available",
          fee: location.base_fee || 0,
        }));

        setLocations(locationData);
      } catch (error) {
        console.error('Error fetching locations:', error);
        setLocations([]);
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [vehicleId, type]);

  // Fetch time slots when date AND location are selected
  useEffect(() => {
    const fetchTimeSlotsForDate = async () => {
      if (!vehicleId || !selectedDate || !selectedLocation) {
        setTimeSlots([]);
        return;
      }

      setIsLoadingTimeSlots(true);
      try {
        const isPickup = type === "pickup";
        const response = await fetchAvailableTimeSlots(vehicleId, selectedDate, isPickup);

        // Find the selected location's time slots
        const locationData = response.find(loc => loc.location_id === selectedLocation);

        if (locationData && locationData.time_slots) {
          setTimeSlots(locationData.time_slots.map(slot => ({
            time: slot.time,
            available: slot.available,
            fee: slot.fee,
          })));
        } else {
          setTimeSlots([]);
        }
      } catch (error) {
        console.error('Error fetching time slots:', error);
        setTimeSlots([]);
      } finally {
        setIsLoadingTimeSlots(false);
      }
    };

    fetchTimeSlotsForDate();
  }, [vehicleId, selectedDate, selectedLocation, type]);

  const handleLocationSelect = (location) => {
    onLocationSelect(location);
    setIsOpen(false);
  };

  const selectedLocationData = locations.find(
    (loc) => loc.id === selectedLocation,
  );

  return (
    <div className="location-picker">
      <div className="location-field">
        <div
          className="location-field-container"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="location-field-content">
            <div className="location-field-input">
              {selectedLocationData
                ? selectedLocationData.name
                : type === 'pickup'
                  ? t('bookingForm.selectPickupLocationPlaceholder')
                  : t('bookingForm.selectDropoffLocationPlaceholder')}
            </div>
            <div className="location-field-label">{label}</div>
          </div>
          <div className="location-field-icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                stroke="#009B31"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z"
                stroke="#009B31"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {isOpen && (
          <div className="location-dropdown">
            {isLoadingLocations ? (
              <div className="location-loading">
                <span>{t('bookingForm.loadingLocations')}</span>
              </div>
            ) : locations.length === 0 ? (
              <div className="location-empty">
                <span>{t('bookingForm.noLocationsAvailable')}</span>
              </div>
            ) : (
              <div className="location-list">
                {locations.map((location) => (
                  <div
                    key={location.id}
                    className="location-item"
                    onClick={() => handleLocationSelect(location)}
                  >
                    <div className="location-info">
                      <div className="location-name">{location.name}</div>
                      <div className="location-address">{location.address}</div>
                    </div>
                    <div className="location-fee">
                      {location.fee > 0 ? `+€${location.fee}` : "Free"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Time slots section - only show when location is selected */}
      {selectedLocationData && (
        <div className="time-slots">
          <div className="time-slots-label">
            {type === 'pickup' ? t('bookingForm.pickupLabel') : t('bookingForm.dropoffLabel')} {t('bookingForm.times') || 'times'}:
          </div>
          {!selectedDate ? (
            <div className="time-slots-select-date">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 10H3M16 2V6M8 2V6M7.8 22H16.2C17.8802 22 18.7202 22 19.362 21.673C19.9265 21.3854 20.3854 20.9265 20.673 20.362C21 19.7202 21 18.8802 21 17.2V8.8C21 7.11984 21 6.27976 20.673 5.63803C20.3854 5.07354 19.9265 4.6146 19.362 4.32698C18.7202 4 17.8802 4 16.2 4H7.8C6.11984 4 5.27976 4 4.63803 4.32698C4.07354 4.6146 3.6146 5.07354 3.32698 5.63803C3 6.27976 3 7.11984 3 8.8V17.2C3 18.8802 3 19.7202 3.32698 20.362C3.6146 20.9265 4.07354 21.3854 4.63803 21.673C5.27976 22 6.11984 22 7.8 22Z"
                  stroke="#009B31"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{t('bookingForm.selectDatesForTimes')}</span>
            </div>
          ) : isLoadingTimeSlots ? (
            <div className="time-slots-loading">{t('bookingForm.loadingTimes') || 'Loading available times...'}</div>
          ) : timeSlots.length === 0 ? (
            <div className="time-slots-empty">{t('bookingForm.noTimesAvailable') || 'No time slots available for this date'}</div>
          ) : (
            <div className="time-slots-grid">
              {timeSlots.map((slot, index) => (
                <button
                  key={index}
                  className={`time-slot ${selectedTime === slot.time ? "selected" : ""} ${!slot.available ? "unavailable" : ""}`}
                  onClick={() => slot.available && onTimeSelect(slot)}
                  disabled={!slot.available}
                >
                  <div className="time-slot-time">{slot.time}</div>
                  {slot.fee > 0 && slot.available && (
                    <div className="time-slot-fee">+€{slot.fee}</div>
                  )}
                  {!slot.available && (
                    <div className="time-slot-unavailable">Full</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
